import logoPodio from 'assets/img/2026/logo_podio.png';
import React, { useEffect, useState } from 'react';
import { buscarIniciativasVencedoras } from 'services/iniciativa.service';
import { IniciativaFormatada } from 'types/iniciativa';
import { IniciativaCardElbb } from './IniciativaCardElbb';
import './PodioElbb.scss';

// Frontend-only: enquanto o backend de vencedores ELBB não responde, mostramos
// um pódio simulado para que a UI fique completa em qualquer momento.
const MOCK_FINALISTAS_ELBB: Array<Partial<IniciativaFormatada>> = [
  {
    id: 'mock_elbb_1',
    titulo: 'Atendimento Expresso na Agência Digital',
    descricao:
      'Redesenho do fluxo de atendimento de pessoas jurídicas nas agências digitais, reduzindo o tempo de espera em 40%.',
    autores: 'Mariana Costa, Roberto Souza, Fernando Lima',
    categoria: 'Prêmio ELBB',
    categoryPremio: 'ELBB',
    corCategoria: 'blue-bb-6',
    totalElogios: 35,
    totalFavoritos: 23,
    totalCompartilhamentos: 12,
    participantes: [
      { nome: 'Mariana Costa', matricula: '1111111', confirmado: true, isOwner: true },
      { nome: 'Roberto Souza', matricula: '2222222', confirmado: true },
      { nome: 'Fernando Lima', matricula: '3333333', confirmado: true },
    ],
  },
  {
    id: 'mock_elbb_2',
    titulo: 'Portal Conectividade Agro ELBB',
    descricao:
      'Plataforma integrada que conecta produtores rurais a serviços de crédito e suporte técnico imediato via inteligência artificial.',
    autores: 'Juliana Dias, André Santos, Bruno Rocha',
    categoria: 'Prêmio ELBB',
    categoryPremio: 'ELBB',
    corCategoria: 'blue-bb-6',
    totalElogios: 28,
    totalFavoritos: 19,
    totalCompartilhamentos: 8,
    participantes: [
      { nome: 'Juliana Dias', matricula: '4444444', confirmado: true, isOwner: true },
      { nome: 'André Santos', matricula: '5555555', confirmado: true },
      { nome: 'Bruno Rocha', matricula: '6666666', confirmado: true },
    ],
  },
  {
    id: 'mock_elbb_3',
    titulo: 'App BB Acessível para ELBB',
    descricao:
      'Conjunto de novas ferramentas de acessibilidade integradas ao aplicativo móvel para deficientes visuais e idosos.',
    autores: 'Carla Silveira, Diego Mendes',
    categoria: 'Prêmio ELBB',
    categoryPremio: 'ELBB',
    corCategoria: 'blue-bb-6',
    totalElogios: 22,
    totalFavoritos: 15,
    totalCompartilhamentos: 6,
    participantes: [
      { nome: 'Carla Silveira', matricula: '7777777', confirmado: true, isOwner: true },
      { nome: 'Diego Mendes', matricula: '8888888', confirmado: true },
    ],
  },
];

export const PodioElbb: React.FC = () => {
  const [iniciativas, setIniciativas] = useState<IniciativaFormatada[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function carregarVencedores() {
      try {
        setLoading(true);
        const data = await buscarIniciativasVencedoras();
        const apenasElbb = data
          .filter((ini) => ini.categoryPremio === 'ELBB')
          .sort((a, b) => {
            const posA = a.position ?? Number.POSITIVE_INFINITY;
            const posB = b.position ?? Number.POSITIVE_INFINITY;
            if (posA !== posB) return posA - posB;
            return b.totalElogios - a.totalElogios;
          });

        const preenchidas: IniciativaFormatada[] = [...apenasElbb];
        for (let i = preenchidas.length; i < 3; i++) {
          preenchidas.push(MOCK_FINALISTAS_ELBB[i] as IniciativaFormatada);
        }

        setIniciativas(preenchidas.slice(0, 3));
      } catch (error) {
        console.error('Erro ao carregar pódio ELBB:', error);
        setIniciativas(MOCK_FINALISTAS_ELBB.slice(0, 3) as IniciativaFormatada[]);
      } finally {
        setLoading(false);
      }
    }

    carregarVencedores();
  }, []);

  if (loading) {
    return (
      <div className="podio-elbb-loading">
        <div className="spinner" />
        <p>Carregando pódio das iniciativas ELBB...</p>
      </div>
    );
  }

  if (iniciativas.length < 3) return null;

  // Mesma ordem visual do protótipo: 2º (esquerda), 1º (centro), 3º (direita).
  const posicoes = [
    { colocacao: 2, label: '2º LUGAR', iniciativa: iniciativas[1], styleClass: 'silver', pedestalClass: 'podio-elbb__step--2' },
    { colocacao: 1, label: '1º LUGAR', iniciativa: iniciativas[0], styleClass: 'gold', pedestalClass: 'podio-elbb__step--1' },
    { colocacao: 3, label: '3º LUGAR', iniciativa: iniciativas[2], styleClass: 'bronze', pedestalClass: 'podio-elbb__step--3' },
  ];

  return (
    <div className="podio-elbb-container">
      <div className="podio-elbb-layout">
        {posicoes.map(({ colocacao, label, iniciativa, styleClass, pedestalClass }) => {
          if (!iniciativa) return null;

          return (
            <div
              key={iniciativa.id}
              className={`podio-elbb-coluna podio-elbb-coluna--${styleClass}`}
            >
              <div className={`podio-elbb-card podio-elbb-card--${styleClass}`}>
                <IniciativaCardElbb
                  {...iniciativa}
                  classificadaEtapa1
                  classificadaEtapa2={false}
                  classificadaIcon={
                    <img
                      src={logoPodio}
                      alt=""
                      aria-hidden="true"
                      className="podio-elbb-card__logo"
                    />
                  }
                  classificadaLabel={label}
                />
              </div>

              <div className={`podio-elbb__step ${pedestalClass}`}>
                <div className="podio-elbb__step-number">{colocacao}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
